import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EmptyState } from "../../components/ui/emptyState/EmptyState";
import { StatusBadge } from "../../components/ui/statusBadge/StatusBadge";
import { useModal } from "../../components/ui/modal/ModalProvider";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { MOCK_DATA, useMockDataVersion } from "../../data/mockDataStore";
import { date } from "../../utils/format";

const CATEGORIES = ["Onboarding", "Service client", "Marketing", "Finance", "Nouveautés"];

export function Academie() {
  useMockDataVersion();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openModal, closeModal, confirm } = useModal();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [statut, setStatut] = useState("");

  const filtered = useMemo(() => {
    return MOCK_DATA.formations.filter((f) => {
      if (q && !(f.titre.toLowerCase().includes(q.toLowerCase()) || f.description.toLowerCase().includes(q.toLowerCase()))) return false;
      if (cat && f.categorie !== cat) return false;
      if (statut && f.statut !== statut) return false;
      return true;
    });
  }, [q, cat, statut]);

  const openForm = (existing) => {
    const f = existing || { titre: "", description: "", categorie: "Onboarding", url: "", statut: "Brouillon" };
    openModal({
      title: existing ? "Modifier la formation" : "Nouvelle formation",
      size: "lg",
      body: (
        <>
          <div className="form-row"><label className="label">Titre</label><input className="input" defaultValue={f.titre} placeholder="Titre de la formation" /></div>
          <div className="form-row row-2">
            <div>
              <label className="label">Catégorie</label>
              <select className="input" defaultValue={f.categorie}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Statut</label>
              <select className="input" defaultValue={f.statut}>
                <option>Brouillon</option>
                <option>Publiée</option>
              </select>
            </div>
          </div>
          <div className="form-row"><label className="label">URL de la vidéo</label><input className="input" placeholder="https://…" defaultValue={f.url || ""} /></div>
          <div className="form-row"><label className="label">Miniature (URL)</label><input className="input" placeholder="https://…" /></div>
          <div className="form-row"><label className="label">Description</label><textarea className="textarea" defaultValue={f.description} placeholder="Description courte…"></textarea></div>
        </>
      ),
      footer: (
        <>
          <button className="btn" onClick={closeModal}>Annuler</button>
          <button className="btn btn-primary" data-testid="formation-save" onClick={() => { closeModal(); toast(existing ? "Formation modifiée" : "Formation ajoutée", "success"); }}>Enregistrer</button>
        </>
      ),
    });
  };

  useEffect(() => {
    if (searchParams.get("new")) openForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPlay = (f) => {
    openModal({
      title: f.titre,
      size: "lg",
      body: (
        <>
          <div style={{ aspectRatio: "16/9", background: "#000", borderRadius: "var(--radius)", display: "grid", placeItems: "center", color: "#fff", fontSize: 48 }}>
            <i className="fa-solid fa-play-circle"></i>
          </div>
          <div style={{ marginTop: 12, color: "var(--text-2)" }}>{f.description}</div>
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-3)" }}>{f.categorie} · {f.duree} · Publiée le {date(f.publie)}</div>
        </>
      ),
    });
  };

  const deleteFormation = () => {
    confirm({
      title: "Supprimer la formation",
      message: "Cette action est irréversible.",
      confirmText: "Supprimer",
      type: "danger",
      onConfirm: () => toast("Formation supprimée", "success"),
    });
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><a onClick={() => navigate("/dashboard")}>Dashboard</a><i className="fa-solid fa-chevron-right"></i>Académie</div>
          <h1 className="page-title">Académie vivaPilot</h1>
          <div className="page-sub">Contenus de formation pour les partenaires</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" data-testid="btn-new-formation" onClick={() => openForm()}><i className="fa-solid fa-plus"></i> Ajouter une formation</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="toolbar" style={{ border: 0 }}>
          <div className="search"><i className="fa-solid fa-magnifying-glass"></i><input data-testid="aca-search" placeholder="Rechercher une formation…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <select className="select" data-testid="aca-cat" value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">Toutes catégories</option>
            {[...new Set(MOCK_DATA.formations.map((f) => f.categorie))].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="select" data-testid="aca-statut" value={statut} onChange={(e) => setStatut(e.target.value)}>
            <option value="">Tous statuts</option>
            <option>Publiée</option>
            <option>Brouillon</option>
          </select>
          <div className="filter-chips"><span className="badge outline"><i className="fa-solid fa-video"></i> {filtered.length} formations</span></div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card"><EmptyState title="Aucune formation" sub="Ajustez vos filtres ou créez une nouvelle vidéo." /></div>
      ) : (
        <div className="video-grid">
          {filtered.map((f) => (
            <div className="video-card" key={f.id} data-testid={`video-${f.id}`}>
              <div className="video-thumb"><i className={`fa-solid ${f.icon}`}></i><span className="duration">{f.duree}</span></div>
              <div className="video-meta">
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <span className="badge accent">{f.categorie}</span>
                  <StatusBadge statut={f.statut} />
                </div>
                <div className="video-title">{f.titre}</div>
                <div className="video-desc">{f.description}</div>
                <div className="video-foot">
                  <div className="muted" style={{ fontSize: 12 }}>{date(f.publie)}</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="icon-btn" data-testid={`video-play-${f.id}`} title="Consulter" onClick={() => openPlay(f)}><i className="fa-solid fa-play"></i></button>
                    <button className="icon-btn" data-testid={`video-edit-${f.id}`} title="Modifier" onClick={() => openForm(f)}><i className="fa-solid fa-pen"></i></button>
                    <button className="icon-btn" data-testid={`video-del-${f.id}`} title="Supprimer" onClick={deleteFormation}><i className="fa-solid fa-trash" style={{ color: "var(--danger)" }}></i></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default Academie;
